// contexts/SessionContext.tsx
"use client";

import React, { createContext, useContext, useEffect, useState } from 'react';
import { authClient } from '../lib/auth-client';

interface SessionContextType {
  session: any;
  userData: any;
  isPending: boolean;
  isReady: boolean;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

// Cache keys
const CACHE_KEYS = {
  SESSION: 'cached_session',
  USER_DATA: 'cached_user_data',
  TIMESTAMP: 'cache_timestamp'
};

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const { data: session, isPending } = authClient.useSession();
  const [userData, setUserData] = useState<any>(null);
  const [isReady, setIsReady] = useState(false);

  // Load from cache immediately on mount
  useEffect(() => {
    // ✅ Guard against SSR
    if (typeof window === 'undefined') return;

    try {
      const cachedUserData = localStorage.getItem(CACHE_KEYS.USER_DATA);
      const cacheTimestamp = localStorage.getItem(CACHE_KEYS.TIMESTAMP);
      
      if (cachedUserData && cacheTimestamp) {
        const age = Date.now() - parseInt(cacheTimestamp);
        
        // Use cache if less than 5 minutes old
        if (age < CACHE_DURATION) {
          setUserData(JSON.parse(cachedUserData));
          setIsReady(true);
        }
      }
    } catch (error) {
      console.error('Error loading from cache:', error);
      // Clear corrupted cache
      localStorage.removeItem(CACHE_KEYS.USER_DATA);
      localStorage.removeItem(CACHE_KEYS.TIMESTAMP);
    }
  }, []);

  // When session loads, fetch or update user data if needed
  useEffect(() => {
    // ✅ Guard against SSR
    if (typeof window === 'undefined') return;

    if (!isPending && session?.user?.email) {
      try {
        const cachedUserData = localStorage.getItem(CACHE_KEYS.USER_DATA);
        const cacheTimestamp = localStorage.getItem(CACHE_KEYS.TIMESTAMP);

        // If no cache or cache is old, you could fetch fresh data here
        // For now, just use session data
        if (!cachedUserData || !cacheTimestamp) {
          const newUserData = {
            email: session.user.email,
            name: session.user.name,
            role: session.user.role,
            // Add other user fields as needed
          };

          setUserData(newUserData);
          localStorage.setItem(CACHE_KEYS.USER_DATA, JSON.stringify(newUserData));
          localStorage.setItem(CACHE_KEYS.TIMESTAMP, Date.now().toString());
        }
        
        setIsReady(true);
      } catch (error) {
        console.error('Error caching user data:', error);
        setIsReady(true); // Still set ready even if caching fails
      }
    } else if (!isPending && !session) {
      // ✅ No session - clear cache and set ready
      try {
        localStorage.removeItem(CACHE_KEYS.USER_DATA);
        localStorage.removeItem(CACHE_KEYS.TIMESTAMP);
      } catch (error) {
        console.error('Error clearing cache:', error);
      }
      setUserData(null);
      setIsReady(true);
    }
  }, [session, isPending]);

  const value: SessionContextType = {
    session,
    userData,
    isPending,
    isReady,
  };

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
}

// Custom hook to use session context
export function useSessionContext() {
  const context = useContext(SessionContext);
  if (context === undefined) {
    throw new Error('useSessionContext must be used within a SessionProvider');
  }
  return context;
}